/* eslint-disable react/jsx-pascal-case */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { setCookie, getCookie, useInterval } from "../components/Common";
import { Palette, API_BASE } from "../AppConfig";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import BSL_Dialog from "../components/BSL_Dialog";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import * as APP from "../redux_store/appState";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        paddingTop: "105px",
    },
    announcement: {
        background: "#eaeaea",
        display: "flex",
        minHeight: "60px",
        flexDirection: "row",
        padding: "20px 20px 20px 140px",
        borderBottom: "1px solid #192E61",
    },
    announcement_designer: {
        background: "#eaeaea",
        display: "flex",
        minHeight: "30px",
        flexDirection: "row",
        padding: "3px 0px 0px 100px",
        borderBottom: "1px solid #192E61",
        alignItems: "center",
    },
    warning: {
        background: "#fdf4e7",
        borderBottom: "1px solid #192E61",
    },
    image: {
        height: "80px",
        maxHeight: "80px",
        padding: "0px 20px 0px 0px",
    },
    image_designer: {
        height: "30px",
        maxHeight: "30px",
        padding: "0px 20px 0px 0px",
    },
    content_container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
    },
    header: {
        color: "#555555",
        padding: "3px",
    },
    content: {
        color: "#333333",
        padding: "3px",
        fontSize: "0.85rem",
    },
    link: {
        padding: "3px",
    },
    closeButton: {
        height: "32px",
        width: "32px",
        position: "absolute",
        right: "20px",
    },
    closeButton_designer: {
        height: "32px",
        width: "32px",
        position: "relative",
        marginLeft: "-12px",
        marginRight: "6px",
    },
});

export default function Announcements(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { previewAnnouncement, warningMessages } = props;
    const reduxRows = useSelector((state) => state.app.announcements);
    const rows = previewAnnouncement ? [previewAnnouncement] : reduxRows;
    const page = useSelector((state) => state.app.page);
    const toolDrawerWidth = useSelector((state) => state.app?.designer?.[APP.TOOL_DRAWER_WIDTH]);
    const SELECT_POINT = useSelector((state) => state.app.designer.designer2D[APP.SELECT_POINT]);
    const orderStatus = useSelector((state) => state.job?.OrderStatus);
    const locked = useSelector((state) => state.job?.App?.Locked);
    const linkRef = React.useRef();
    const [dialogData, setDialogData] = React.useState(false);
    const dismissedCookie = previewAnnouncement ? null : getCookie("dismissed");
    const customListIsOpen = useSelector((state) => state.app?.designer?.[APP.CUSTOM_COMPONENT_LIST_IS_OPEN]) ?? false;
    const hasCustomBOM =
        (useSelector((state) => state.job?.QuoteParameters?.BOM?.OrderList?.items?.length) ?? 0) > 0 ||
        customListIsOpen;
    const isDesigner = page === APP.DESIGNER_PAGE;
    const hasOrderPanel = isDesigner && orderStatus != null && orderStatus !== "";
    const rightPadding = (isDesigner && toolDrawerWidth ? toolDrawerWidth : 0) + 10;
    const serverIsAlerting = useSelector((state) => state.app?.serverHealth === "alerting");
    const [dismissedAnnouncements, setDismissedAnnouncements] = React.useState(() => {
        try {
            return dismissedCookie ? JSON.parse(dismissedCookie) : {};
        } catch {
            return {};
        }
    });

    useInterval(() => dispatch(APP.updateAnnouncements()), previewAnnouncement ? null : 1 * 60_000);

    const dismissAnnouncement = (idPlusValidTo) => {
        if (previewAnnouncement) return;

        const now = Date.now();
        const newMap = { ...dismissedAnnouncements };

        newMap[idPlusValidTo] = now;

        setDismissedAnnouncements(newMap);
        setCookie("dismissed", JSON.stringify(newMap));
    };

    const resetAutoDisplayAnnouncement = () => {
        if (!rows) return;

        // Remove dismissed announcements that are auto-displayed and were dismissed more than 1 hour ago
        const oneHourAgo = Date.now() - 3600000;
        const newDismissed = {};

        rows.forEach((row) => {
            const key = row.id + row.valid_to_ts;
            const timestamp = dismissedAnnouncements[key];
            const isDismissed = timestamp !== undefined;
            const isAutoDisplayed = row.auto_display_trigger && row.auto_display_trigger !== "None";

            if (isDismissed) {
                if (!isAutoDisplayed || (timestamp && timestamp > oneHourAgo)) {
                    newDismissed[key] = timestamp;
                }
            }
        });

        setDismissedAnnouncements(newDismissed);
        setCookie("dismissed", JSON.stringify(newDismissed));
    };

    React.useEffect(() => {
        if (previewAnnouncement) return;
        resetAutoDisplayAnnouncement();
    }, [rows]);

    if (!rows || rows.length === 0 || SELECT_POINT) {
        return <div className={classes.root}>&nbsp;</div>;
    }

    const rendered = rows
        .filter((a) => {
            if (previewAnnouncement) return true;

            const key = a.id + a.valid_to_ts;
            const timestamp = dismissedAnnouncements[key];
            const isDismissed = timestamp !== undefined;

            const { auto_display_trigger: trigger } = a;

            if (isDismissed) return false;
            if (!trigger || trigger === "None") return true;

            switch (trigger) {
                case "System Outage":
                    return serverIsAlerting;
                // Add other cases as needed
                default:
                    return false;
            }
        })
        .map((announcement) => {
            let imgUrl = announcement.image;
            if (imgUrl && !imgUrl.includes("http")) imgUrl = `${API_BASE()}document/APP_announcements/${imgUrl}`;

            const containerClass =
                (isDesigner ? classes.announcement_designer : classes.announcement) +
                (announcement.type === "warning" ? ` ${classes.warning}` : "");

            return (
                <div key={announcement.id} className={containerClass}>
                    {isDesigner && !previewAnnouncement && (
                        <IconButton
                            className={classes.closeButton_designer}
                            onClick={() => dismissAnnouncement(announcement.id + announcement.valid_to_ts)}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}

                    {announcement.image && (
                        <img className={isDesigner ? classes.image_designer : classes.image} src={imgUrl} alt="" />
                    )}
                    {announcement.type === "warning" && (
                        <ReportProblemOutlinedIcon
                            style={
                                isDesigner
                                    ? { color: "#ff9e00", width: 20, height: 24, padding: "0 2px", marginRight: 8 }
                                    : { color: "#ff9e00", width: 48, height: 48, padding: "6px 12px" }
                            }
                        />
                    )}

                    <div className={classes.content_container} style={isDesigner ? { flexDirection: "row" } : {}}>
                        {announcement.header && (
                            <h3
                                className={classes.header}
                                style={{
                                    color: "#333",
                                    margin: 0,
                                    fontWeight: "bold",
                                    fontSize: isDesigner ? 11 : 18,
                                }}
                            >
                                {announcement.header}
                            </h3>
                        )}
                        {announcement.content && (
                            <Typography
                                component="p"
                                variant="caption"
                                className={classes.content}
                                style={{
                                    fontSize: isDesigner ? "0.65rem" : "0.85rem",
                                    paddingRight:
                                        announcement.action_link || announcement.action_content ? 0 : rightPadding,
                                }}
                            >
                                {announcement.content}
                            </Typography>
                        )}

                        {(announcement.action_link || announcement.action_content) && (
                            <div className={classes.link} style={{ paddingRight: rightPadding }}>
                                {announcement.action_content && (
                                    <Link
                                        style={{
                                            color: Palette.FieldersBlue,
                                            cursor: "pointer",
                                            fontSize: isDesigner ? "0.65rem" : "9pt",
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setDialogData({
                                                title: announcement.header,
                                                message: [<>{announcement.action_content}</>],
                                                secondaryActionText:
                                                    announcement.action_text && announcement.action_link
                                                        ? announcement.action_text
                                                        : false,
                                                secondaryAction: () => linkRef.current.click(),
                                            });
                                        }}
                                    >
                                        more info…
                                    </Link>
                                )}
                                <a
                                    ref={linkRef}
                                    href={announcement.action_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: Palette.FieldersBlue,
                                        display: announcement.action_content ? "none" : "block",
                                    }}
                                >
                                    {announcement.action_text || announcement.action_link}
                                </a>
                            </div>
                        )}
                    </div>

                    {!isDesigner && !previewAnnouncement && (
                        <IconButton
                            className={classes.closeButton}
                            onClick={() => dismissAnnouncement(announcement.id + announcement.valid_to_ts)}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            );
        });

    if (rendered.length === 0) return <div className={classes.root}>&nbsp;</div>;

    const shouldHaveExtraTopPadding = hasOrderPanel || locked || warningMessages?.length > 0 || hasCustomBOM;
    const wrapperStyle = isDesigner
        ? {
              zIndex: 50,
              position: "relative",
              paddingTop: shouldHaveExtraTopPadding ? 132 : 85,
          }
        : {};

    return (
        <div className={classes.root} style={wrapperStyle}>
            {rendered}
            <BSL_Dialog
                title={dialogData.title}
                open={Boolean(dialogData)}
                onClose={() => setDialogData(false)}
                button1Text={dialogData?.primaryActionText || "OK"}
                button1Click={() => {
                    setDialogData(false);
                    if (dialogData?.primaryAction) dispatch(dialogData.primaryAction);
                }}
                button2Text={dialogData?.secondaryActionText}
                button2Click={() => {
                    setDialogData(false);
                    if (dialogData?.secondaryAction) dispatch(dialogData.secondaryAction);
                }}
            >
                {dialogData.message}
            </BSL_Dialog>
        </div>
    );
}
